import { FC, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSelector } from 'react-redux';

import { GridRow, GridCol } from 'dibs-elements/exports/Grid';
import { FormattedMessage } from 'dibs-react-intl';

import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { PdpSharedDescriptionAbout } from '../PdpSharedDescriptions/PdpSharedDescriptionAbout';
import { PdpRespDetails } from '../PdpRespDetails/PdpRespDetails';
import { RelayVariables, Store } from '../../global/relayVariableTypes';
import { showPricingGuidance } from '../PdpSharedPricingGuidance/pricingGuidanceHelpers';

import { PdpRespAboutItemPanel_item$key } from './__generated__/PdpRespAboutItemPanel_item.graphql';
import { PdpRespAboutItemPanel_user$key } from './__generated__/PdpRespAboutItemPanel_user.graphql';

import styles from './PdpRespAboutItemPanel.scss';

const PdpRespPricingGuidanceLazy = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespPricingGuidance" */ '../PdpRespDetails/PdpRespPricingGuidance'
        )
);

type Props = {
    showHeader?: boolean;
    item: PdpRespAboutItemPanel_item$key;
    user: PdpRespAboutItemPanel_user$key;
    currency: string;
    selectedSkuId: string | null;
    showExpandingArea?: boolean;
    dataTn?: string;
};

export const aboutHeader = (
    <FormattedMessage id="PdpRespAboutItemPanel.aboutHeader" defaultMessage="About the Item" />
);

const itemFragment = graphql`
    fragment PdpRespAboutItemPanel_item on Item {
        ...PdpSharedDescriptionAbout_item
        ...PdpRespDetails_item
        ...PdpRespPricingGuidance_item @include(if: $isClient)
        ...pricingGuidanceHelpers_item
    }
`;

const userFragment = graphql`
    fragment PdpRespAboutItemPanel_user on User {
        ...PdpRespDetails_user
        ...PdpRespPricingGuidance_user @include(if: $isClient)
    }
`;

export const PdpRespAboutItemPanel: FC<Props> = ({
    showHeader,
    item: itemRef,
    user: userRef,
    currency,
    selectedSkuId,
    showExpandingArea,
    dataTn,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const { isTablet } = useServerVarsContext();

    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );

    const { isClient } = relayVariables;

    const showPricingGuidanceGraph = showPricingGuidance({ item, currency });

    // For details collapsed height = [# of lines to show] * ([line-height] + [margin height]) - [margin of container]
    const singleColumnDetailsHeight = 9 * (24 + 9) - 9;

    return (
        <GridRow dataTn={dataTn}>
            <GridCol
                desktop={showPricingGuidanceGraph ? 4 : 5}
                tabletPortrait={showPricingGuidanceGraph ? 6 : 12}
                tabletLandscape={showPricingGuidanceGraph ? 6 : undefined}
            >
                <div className={styles.descriptionWrapper}>
                    <PdpSharedDescriptionAbout
                        item={item}
                        headerClass={showHeader ? styles.headerText : styles.isHidden}
                        contentClass={styles.paragraphText}
                        title={aboutHeader}
                        collapsedHeight={300}
                        showExpandingArea={showExpandingArea}
                    />
                </div>
            </GridCol>
            {isClient && isTablet && (
                <Suspense fallback="">
                    <PdpRespPricingGuidanceLazy
                        currency={currency}
                        item={item}
                        user={user}
                        skuId={selectedSkuId}
                    />
                </Suspense>
            )}
            <GridCol
                desktop={showPricingGuidanceGraph ? 4 : 6}
                desktopOptions={
                    !showPricingGuidanceGraph
                        ? {
                              offset: 1,
                          }
                        : undefined
                }
                tabletPortrait={12}
                tabletLandscape={12}
            >
                {/* Details */}
                <PdpRespDetails
                    variant={
                        showPricingGuidanceGraph && !isTablet
                            ? 'pdpRespTabsVariant'
                            : 'pdpRedesignVariant'
                    }
                    showHeader={false}
                    isClient={isClient}
                    currency={currency}
                    item={item}
                    selectedSkuId={selectedSkuId}
                    user={user}
                    collapsedHeight={singleColumnDetailsHeight}
                    showTopBorder={isTablet}
                />
            </GridCol>
            {isClient && !isTablet && (
                <Suspense fallback="">
                    <PdpRespPricingGuidanceLazy
                        currency={currency}
                        item={item}
                        user={user}
                        skuId={selectedSkuId}
                    />
                </Suspense>
            )}
        </GridRow>
    );
};
